import React, { forwardRef, type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Heading, type HeadingTagType } from "@purpur/heading";
import * as RadixRadioGroup from "@radix-ui/react-radio-group";
import c from "classnames/bind";

import styles from "./radio-button-group.module.scss";
import { RadioButtonGroupItem } from "./radio-button-group-item";

export const RADIO_BUTTON_GROUP_DIRECTION = ["ltr", "rtl"] as const;
export const RADIO_BUTTON_GROUP_ORIENTATION = ["horizontal", "vertical"] as const;

const cx = c.bind(styles);

export type RadioButtonGroupRootProps = Omit<BaseProps, "id"> & {
  /**
   * ID of the radio group container.
   * */
  id: string;
  /**
   * To use when no heading is given.
   * */
  ["aria-label"]?: string;
  /**
   * The value of the radio item that should be checked when initially rendered. Use when you do not need to control the state of the radio items.
   * */
  defaultValue?: string;
  /**
   * The reading direction of the radio group. If omitted, it assumes LTR (left-to-right) reading mode.
   * */
  dir?: (typeof RADIO_BUTTON_GROUP_DIRECTION)[number];
  /**
   * When true, prevents the user from interacting with radio items.
   * */
  disabled?: boolean;
  /**
   * The radio items.
   *
      RadioButtonGroupItem = {
        ["data-testid"]?: string;
        disabled?: boolean;
        helperText?: string;
        id: string;
        label: string;
        ref?: Ref<HTMLButtonElement>;
        value: string;
      }[]
   * */
  items?: RadioButtonGroupItem[];
  /**
   * Radio group children. Best used with `RadioButtonGroup.Item`.
   */
  children?: ReactNode;
  /**
   * When true, keyboard navigation will loop from last item to first, and vice versa.
   * */
  loop?: boolean;
  /**
   * The name of the group. Submitted with its owning form as part of a name/value pair.
   * */
  name?: string;
  /**
   * Event handler called when the value changes.
   * */
  onValueChange?: (value: string) => void;
  /**
   * The orientation of the component.
   * */
  orientation?: (typeof RADIO_BUTTON_GROUP_ORIENTATION)[number];
  /**
   * When true, indicates that the user must check a radio item before the owning form can be submitted.
   * */
  required?: boolean;
  /**
   * The controlled value of the radio item to check. Should be used in conjunction with onValueChange.
   * */
  value?: string;
  /**
   * Renders above the radio group as a heading.
   * */
  heading?: string;
  /**
   * Changes heading level for heading.
   */
  headingTag?: HeadingTagType;
};

const rootClassName = "purpur-radio-button-group";

export const RadioButtonGroupRoot = forwardRef<HTMLDivElement, RadioButtonGroupRootProps>(
  (
    {
      id,
      "data-testid": dataTestId,
      className,
      items,
      heading,
      loop = true,
      orientation = "vertical",
      headingTag = "h2",
      children,
      ...props
    },
    ref
  ) => {
    if (!items?.length && !children) {
      return null;
    }

    const itemsRenderer = () => {
      return items?.map((item) => (
        <RadioButtonGroupItem
          key={`radio-item-${item.value}`}
          disabled={props.disabled}
          {...item}
        />
      ));
    };

    return (
      <div className={cx(className, `${rootClassName}__container`)}>
        {heading && (
          <Heading
            tag={headingTag}
            data-testid={dataTestId ? `${dataTestId}-heading` : undefined}
            id={`${id}-heading`}
            variant="subsection-100"
          >
            {props.required && <span aria-hidden>{"* "}</span>}
            {heading}
          </Heading>
        )}
        <RadixRadioGroup.Root
          {...props}
          aria-labelledby={heading ? `${id}-heading` : undefined}
          className={cx(rootClassName)}
          data-testid={dataTestId}
          loop={loop}
          orientation={orientation}
          ref={ref}
        >
          {items?.length ? itemsRenderer() : children}
        </RadixRadioGroup.Root>
      </div>
    );
  }
);

RadioButtonGroupRoot.displayName = "RadioButtonGroupRoot";
